import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '../firebase'

// Define role hierarchy and default permissions
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CUSTOMER_ADMIN: 'customer_admin', 
  DEVICE_ADMIN: 'device_admin',
  USER_ADMIN: 'user_admin',
  REPORTS_ADMIN: 'reports_admin',
  DEVICE_VIEWER: 'device_viewer',
  REPORTS_VIEWER: 'reports_viewer',
  REPLENISH_WORKER: 'replenish_worker'
}

export const PERMISSIONS = {
  DEVICES: {
    VIEW: 'devices.view',
    EDIT: 'devices.edit', 
    ADD: 'devices.add',
    DELETE: 'devices.delete',
    MAINTENANCE: 'devices.maintenance',
    LOGS: 'devices.logs',
    SERVICE_PIN: 'devices.service_pin',
    PAYMENT_CONFIG: 'devices.payment_config',
    LAYOUT_EDIT: 'devices.layout_edit',
    SLOT_CONFIG: 'devices.slot_config',
    CLEAR_LOCKOUTS: 'devices.clear_lockouts',
    RESTOCK: 'devices.restock',
    REPLENISH: 'devices.replenish'
  },
  REPORTS: {
    VIEW: 'reports.view',
    EXPORT: 'reports.export',
    ANALYTICS: 'reports.analytics',
    CASH_TRANSACTIONS: 'reports.cash_transactions',
    PAYMENT_GATEWAY: 'reports.payment_gateway',
    REFUNDS: 'reports.refunds'
  },
  USERS: {
    VIEW: 'users.view',
    INVITE: 'users.invite',
    EDIT: 'users.edit',
    DELETE: 'users.delete'
  },
  INVENTORY: {
    VIEW: 'inventory.view',
    EDIT: 'inventory.edit',
    MANAGE: 'inventory.manage',
    IMPORT: 'inventory.import',
    EXPORT: 'inventory.export'
  },
  DIAGNOSE: {
    VIEW: 'diagnose.view',
    RESOLVE: 'diagnose.resolve'
  },
  NOTIFICATIONS: {
    VIEW: 'notifications.view',
    CONFIGURE: 'notifications.configure'
  },
  DOWNLOADS: {
    ACCESS: 'downloads.access'
  }
}

// Default role permissions mapping
export const ROLE_PERMISSIONS = {
  [ROLES.CUSTOMER_ADMIN]: [
    // All permissions for customer admin
    ...Object.values(PERMISSIONS.DEVICES),
    ...Object.values(PERMISSIONS.REPORTS),
    ...Object.values(PERMISSIONS.USERS),
    ...Object.values(PERMISSIONS.INVENTORY),
    ...Object.values(PERMISSIONS.DIAGNOSE),
    ...Object.values(PERMISSIONS.NOTIFICATIONS),
    ...Object.values(PERMISSIONS.DOWNLOADS)
  ],
  [ROLES.DEVICE_ADMIN]: [
    PERMISSIONS.DEVICES.VIEW,
    PERMISSIONS.DEVICES.EDIT,
    PERMISSIONS.DEVICES.MAINTENANCE,
    PERMISSIONS.DEVICES.LOGS,
    PERMISSIONS.DEVICES.SERVICE_PIN,
    PERMISSIONS.DEVICES.PAYMENT_CONFIG,
    PERMISSIONS.DEVICES.LAYOUT_EDIT,
    PERMISSIONS.DEVICES.SLOT_CONFIG,
    PERMISSIONS.DEVICES.CLEAR_LOCKOUTS,
    PERMISSIONS.DEVICES.RESTOCK,
    PERMISSIONS.DIAGNOSE.VIEW,
    PERMISSIONS.NOTIFICATIONS.VIEW
  ],
  [ROLES.USER_ADMIN]: [
    PERMISSIONS.USERS.VIEW,
    PERMISSIONS.USERS.INVITE,
    PERMISSIONS.USERS.EDIT,
    PERMISSIONS.NOTIFICATIONS.VIEW
  ],
  [ROLES.REPORTS_ADMIN]: [
    PERMISSIONS.REPORTS.VIEW,
    PERMISSIONS.REPORTS.EXPORT,
    PERMISSIONS.REPORTS.ANALYTICS,
    PERMISSIONS.DOWNLOADS.ACCESS
  ],
  [ROLES.DEVICE_VIEWER]: [
    PERMISSIONS.DEVICES.VIEW,
    PERMISSIONS.DEVICES.LOGS
  ],
  [ROLES.REPORTS_VIEWER]: [
    PERMISSIONS.REPORTS.VIEW
  ],
  [ROLES.REPLENISH_WORKER]: [
    PERMISSIONS.DEVICES.VIEW,
    PERMISSIONS.DEVICES.LAYOUT_EDIT,
    PERMISSIONS.DEVICES.REPLENISH,
    PERMISSIONS.DEVICES.RESTOCK
  ]
}

/**
 * Get user permissions from {customer}_users collection
 * @param {string} customer - Customer identifier
 * @param {string} emailId - User's email
 * @returns {Object|null} User data with roles and permissions
 */
export const getUserPermissions = async (customer, emailId) => {
  try {
    const userRef = doc(db, `${customer}_users`, emailId)
    const userSnap = await getDoc(userRef)
    
    if (userSnap.exists()) {
      const userData = userSnap.data()
      
      // Ensure alert_thresholds exist for existing users
      if (!userData.alert_thresholds) {
        const defaultThresholds = {
          stock: {
            critical: 20,
            warning: 50,
            low: 70
          },
          enabled: {
            stockAlerts: true,
            deviceOffline: true,
            maintenance: true,
            toastsEnabled: false
          }
        }
        
        // Update user with default thresholds
        await updateDoc(userRef, { alert_thresholds: defaultThresholds })
        userData.alert_thresholds = defaultThresholds
      }
      
      // Ensure toastsEnabled exists for existing users (backward compatibility)
      if (userData.alert_thresholds && userData.alert_thresholds.enabled && userData.alert_thresholds.enabled.toastsEnabled === undefined) {
        userData.alert_thresholds.enabled.toastsEnabled = false
        await updateDoc(userRef, { 'alert_thresholds.enabled.toastsEnabled': false })
      }
      
      return userData
    }
    
    return null
  } catch (error) {
    console.error('Error fetching user permissions:', error)
    return null
  }
}

/**
 * Create or update user in {customer}_users collection
 * @param {string} customer - Customer identifier  
 * @param {string} emailId - User's email
 * @param {Object} userData - User data (roles, permissions, metadata)
 * @returns {boolean} Success status
 */
export const createOrUpdateUser = async (customer, emailId, userData) => {
  try {
    const userRef = doc(db, `${customer}_users`, emailId)
    
    const userDoc = {
      emailId,
      roles: userData.roles || [ROLES.DEVICE_VIEWER],
      permissions: userData.permissions || getUserPermissionsFromRoles(userData.roles || [ROLES.DEVICE_VIEWER]),
      status: userData.status || 'active',
      created_at: userData.created_at || new Date().toISOString(),
      created_by: userData.created_by || 'system',
      last_login: userData.last_login || null,
      device_restrictions: userData.device_restrictions || [],
      metadata: userData.metadata || {},
      alert_thresholds: userData.alert_thresholds || {
        stock: {
          critical: 20,     // Below 20% = Critical alert
          warning: 50,      // Below 50% = Warning alert  
          low: 70          // Below 70% = Low stock alert
        },
        enabled: {
          stockAlerts: true,
          deviceOffline: true,
          maintenance: true,
          toastsEnabled: false  // Toast notifications disabled by default
        }
      }
    }
    
    await setDoc(userRef, userDoc, { merge: true })
    return true
  } catch (error) {
    console.error('Error creating/updating user:', error)
    return false
  }
}

/**
 * Get permissions array from user roles
 * @param {Array} roles - Array of user roles
 * @returns {Array} Array of permissions
 */
export const getUserPermissionsFromRoles = (roles) => {
  const permissions = new Set()
  
  roles.forEach(role => {
    const rolePermissions = ROLE_PERMISSIONS[role] || []
    rolePermissions.forEach(permission => permissions.add(permission))
  })
  
  return Array.from(permissions)
}

/**
 * Check if user has specific permission
 * @param {Object} user - Firebase user with claims
 * @param {Object} userPermissions - User permissions from {customer}_users collection
 * @param {string} permission - Permission to check
 * @param {string} customer - Customer context
 * @returns {boolean} Has permission
 */
export const hasPermission = (user, userPermissions, permission, customer = null) => {
  // Super admin has all permissions
  if (user?.customClaims?.sadmin) {
    return true
  }
  
  // Check if user has valid customer claim
  const userCustomer = user?.customClaims?.customer
  if (!userCustomer) {
    return false
  }
  
  // If customer context provided, ensure it matches user's claim
  if (customer && userCustomer !== customer) {
    return false
  }
  
  // If no granular permissions found, default to customer_admin role for claim holders
  if (!userPermissions) {
    return ROLE_PERMISSIONS[ROLES.CUSTOMER_ADMIN].includes(permission)
  }
  
  // Check user status
  if (userPermissions.status === 'suspended' || userPermissions.status === 'inactive') {
    return false
  }
  
  // Check granular permissions
  return userPermissions.permissions?.includes(permission) || false
}

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - Firebase user with claims
 * @param {Object} userPermissions - User permissions from {customer}_users collection  
 * @param {Array} permissions - Array of permissions to check
 * @param {string} customer - Customer context
 * @returns {boolean} Has any permission
 */
export const hasAnyPermission = (user, userPermissions, permissions, customer = null) => {
  return permissions.some(permission => hasPermission(user, userPermissions, permission, customer))
}

/**
 * Check if user has all specified permissions
 * @param {Object} user - Firebase user with claims
 * @param {Object} userPermissions - User permissions from {customer}_users collection
 * @param {Array} permissions - Array of permissions to check  
 * @param {string} customer - Customer context
 * @returns {boolean} Has all permissions
 */
export const hasAllPermissions = (user, userPermissions, permissions, customer = null) => {
  return permissions.every(permission => hasPermission(user, userPermissions, permission, customer))
}

/**
 * Get all users for a customer
 * @param {string} customer - Customer identifier
 * @returns {Array} Array of user documents
 */
export const getCustomerUsers = async (customer) => {
  try {
    const usersRef = collection(db, `${customer}_users`)
    const snapshot = await getDocs(usersRef)
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error fetching customer users:', error)
    return []
  }
}

/**
 * Delete user from {customer}_users collection
 * @param {string} customer - Customer identifier
 * @param {string} emailId - User's email
 * @returns {boolean} Success status
 */
export const deleteUser = async (customer, emailId) => {
  try {
    const userRef = doc(db, `${customer}_users`, emailId)
    await deleteDoc(userRef)
    return true
  } catch (error) {
    console.error('Error deleting user:', error)
    return false
  }
}

/**
 * Update user status (active, suspended, inactive)
 * @param {string} customer - Customer identifier
 * @param {string} emailId - User's email
 * @param {string} status - New status
 * @returns {boolean} Success status
 */
export const updateUserStatus = async (customer, emailId, status) => {
  try {
    const userRef = doc(db, `${customer}_users`, emailId)
    await updateDoc(userRef, { 
      status,
      updated_at: new Date().toISOString()
    })
    return true
  } catch (error) {
    console.error('Error updating user status:', error)
    return false
  }
}

/**
 * Update user notification preference
 * @param {string} customer - Customer identifier
 * @param {string} emailId - User's email
 * @param {string} preferenceKey - Preference key (e.g., 'toastsEnabled')
 * @param {any} value - New value for the preference
 * @returns {boolean} Success status
 */
export const updateUserPreference = async (customer, emailId, preferenceKey, value) => {
  try {
    const userRef = doc(db, `${customer}_users`, emailId)
    await updateDoc(userRef, { 
      [`alert_thresholds.enabled.${preferenceKey}`]: value,
      updated_at: new Date().toISOString()
    })
    return true
  } catch (error) {
    console.error('Error updating user preference:', error)
    return false
  }
}

/**
 * Auto-create user record for claim holders on first login
 * @param {string} customer - Customer identifier
 * @param {string} emailId - User's email
 * @param {Object} userInfo - Additional user information
 * @returns {boolean} Success status
 */
export const autoCreateUserRecord = async (customer, emailId, userInfo = {}) => {
  try {
    // Check if user already exists
    const existingUser = await getUserPermissions(customer, emailId)
    if (existingUser) {
      return true // User already exists
    }
    
    // Create default customer_admin record for claim holders
    const userData = {
      roles: [ROLES.CUSTOMER_ADMIN],
      permissions: ROLE_PERMISSIONS[ROLES.CUSTOMER_ADMIN],
      status: 'active',
      created_at: new Date().toISOString(),
      created_by: 'auto_create',
      last_login: new Date().toISOString(),
      metadata: {
        name: userInfo.name || emailId.split('@')[0],
        auto_created: true
      }
    }
    
    return await createOrUpdateUser(customer, emailId, userData)
  } catch (error) {
    console.error('Error auto-creating user record:', error)
    return false
  }
}